import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import 'leaflet/dist/leaflet.css';
import 'react-leaflet-markercluster/styles';
import 'leaflet.markercluster';
import L from 'leaflet';
import { useEffect } from 'react';
import type { ReactNode } from 'react';

// Fix Leaflet's default icon urls (required when bundling with Vite).
delete (L.Icon.Default.prototype as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

export type LeafletPinVariant = 'available' | 'unavailable' | 'default';

export interface LeafletPin {
  id: string;
  lat: number;
  lng: number;
  label?: string;
  body?: ReactNode;
  /** Rich popup content; takes precedence over label/body when set. */
  popup?: ReactNode;
  variant?: LeafletPinVariant;
}

export interface LeafletMapProps {
  pins: LeafletPin[];
  center?: [number, number];
  zoom?: number;
  className?: string;
  height?: number | string;
  /** When true, fit the viewport to all pins (ignores invalid/zero coords). */
  fitToPins?: boolean;
  /** Group nearby pins into numbered clusters when zoomed out. */
  clusterPins?: boolean;
}

const PIN_COLORS: Record<LeafletPinVariant, string> = {
  available: '#22c55e',
  unavailable: '#ef4444',
  default: '#3b82f6',
};

function pinIcon(variant: LeafletPinVariant = 'default') {
  const color = PIN_COLORS[variant];
  return L.divIcon({
    className: '',
    html: `<div style="background:${color};width:16px;height:16px;border-radius:50%;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.35)"></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
    popupAnchor: [0, -10],
  });
}

function clusterIcon(cluster: L.MarkerCluster) {
  const count = cluster.getChildCount();
  const size = count >= 100 ? 52 : count >= 10 ? 44 : 36;
  const fontSize = count >= 100 ? 12 : 14;

  return L.divIcon({
    html: `<div style="width:${size}px;height:${size}px;background:rgba(20,184,166,0.88);color:#fff;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:${fontSize}px;border:2px solid rgba(255,255,255,0.9);box-shadow:0 2px 10px rgba(0,0,0,0.35)">${count}</div>`,
    className: 'eon-marker-cluster-icon',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function MapMarkers({ pins }: { pins: LeafletPin[] }) {
  return (
    <>
      {pins.map((p) => (
        <Marker key={p.id} position={[p.lat, p.lng]} icon={pinIcon(p.variant)}>
          {(p.popup || p.label || p.body) && (
            <Popup maxWidth={320} minWidth={220}>
              {p.popup ?? (
                <>
                  {p.label ? <strong>{p.label}</strong> : null}
                  {p.body ? <div className="mt-1">{p.body}</div> : null}
                </>
              )}
            </Popup>
          )}
        </Marker>
      ))}
    </>
  );
}

function FitBounds({ pins }: { pins: LeafletPin[] }) {
  const map = useMap();

  useEffect(() => {
    if (pins.length === 0) return;
    if (pins.length === 1) {
      const pin = pins[0]!;
      map.setView([pin.lat, pin.lng], 14);
      return;
    }
    const bounds = L.latLngBounds(pins.map((p) => [p.lat, p.lng] as [number, number]));
    map.fitBounds(bounds, { padding: [48, 48], maxZoom: 14 });
  }, [map, pins]);

  return null;
}

export function LeafletMap({
  pins,
  center = [39.92, 32.85],
  zoom = 6,
  className,
  height = 480,
  fitToPins = false,
  clusterPins = true,
}: LeafletMapProps) {
  return (
    <div className={className} style={{ height }}>
      <MapContainer
        center={center}
        zoom={zoom}
        scrollWheelZoom
        style={{ height: '100%', width: '100%', borderRadius: 12 }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {fitToPins && pins.length > 0 ? <FitBounds pins={pins} /> : null}
        {clusterPins && pins.length > 1 ? (
          <MarkerClusterGroup
            chunkedLoading
            showCoverageOnHover={false}
            zoomToBoundsOnClick
            spiderfyOnMaxZoom
            disableClusteringAtZoom={15}
            maxClusterRadius={60}
            iconCreateFunction={clusterIcon}
          >
            <MapMarkers pins={pins} />
          </MarkerClusterGroup>
        ) : (
          <MapMarkers pins={pins} />
        )}
      </MapContainer>
    </div>
  );
}
